import { jsx } from "react/jsx-runtime";
import constate from "constate";
import { useRef, useEffect } from "react";
import "../components/Modal/Alert/AlertModal.js";
import CopyAlertModal from "../components/Modal/Alert/CopyAlertModal.js";
import DeleteAlertModal from "../components/Modal/Alert/DeleteAlertModal.js";
import PasteAlertModal from "../components/Modal/Alert/PasteAlertModal.js";
import { useBulkRowSelection } from "./useBulkRowSelection.js";
import { useColumns } from "./useColumnsContext.js";
import { useDataBufferContext } from "./useDataBuffer.js";
import { useModal } from "../hooks/useModal.js";
import { getSelectedCellRange, rowsAreSelected } from "../hooks/useSelectCells.js";
import { EDITOR_ID, HEADER_MENU_STATE_ATTRIBUTE, getEditedValue } from "../utils/cells.js";
import { useClipboardActionsContext } from "./useClipboardActions.js";
import { GET_ROWS_LIMIT } from "../utils/buffer/TableDataBuffer.js";
const isMetaKeyEvent = (event) => {
  const ev = event;
  return ev.key === "Meta" || ev.key === "Control";
};
const isHasMetaKeyEvent = (event) => {
  const ev = event;
  return ev.metaKey || ev.ctrlKey;
};
const COPY_PASTE_LIMIT = GET_ROWS_LIMIT;
const useKeyActions = ({
  tableId,
  tableRef,
  disabled,
  totalRows,
  additionalRows,
  onDownloadTable
}) => {
  const { selection, getIsBulkSelecting, exitSelectionState } = useBulkRowSelection();
  const { buffer } = useDataBufferContext();
  const metaKey = useRef(false);
  const isBulkSelecting = useRef(!!getIsBulkSelecting && getIsBulkSelecting());
  const bulkSelection = useRef(selection);
  const { columnConfig: columns, getAbsoluteColumnIndex } = useColumns();
  const rowCounts = useRef({ total: totalRows, additional: additionalRows });
  const { onCopy: handleCopy, onPaste: handlePaste } = useClipboardActionsContext();
  const CopyAlert = (close) => /* @__PURE__ */ jsx(CopyAlertModal, { onClose: close, onDownload: onDownloadTable });
  const PasteAlert = (close) => /* @__PURE__ */ jsx(PasteAlertModal, { onClose: close });
  const DeleteAlert = (close) => /* @__PURE__ */ jsx(DeleteAlertModal, { onClose: close });
  const copyAlertModal = useModal({ modal: CopyAlert });
  const pasteAlertModal = useModal({ modal: PasteAlert });
  const deleteAlertModal = useModal({ modal: DeleteAlert });
  useEffect(() => {
    isBulkSelecting.current = !!getIsBulkSelecting && getIsBulkSelecting();
    bulkSelection.current = selection;
  }, [getIsBulkSelecting, selection]);
  useEffect(() => {
    rowCounts.current = { total: totalRows, additional: additionalRows };
  }, [totalRows, additionalRows]);
  const buildCellChange = ({
    position,
    column,
    columnKey,
    value,
    row,
    disabled: disabled2,
    columnDisabled,
    ghost
  }) => {
    return {
      position,
      column,
      columnKey,
      value,
      ghost,
      disabled: disabled2,
      columnDisabled,
      id: row ? row.id : (position + 1).toString(),
      rowIndex: row ? row.rowIndex : position + 1
    };
  };
  const getTableContainsEvent = (event) => {
    var _a;
    return !!((_a = tableRef == null ? void 0 : tableRef.current) == null ? void 0 : _a.contains(event == null ? void 0 : event.target));
  };
  const getPreventKeyActions = (event, allowBodyAction) => {
    const tableContainsEvent = getTableContainsEvent(event);
    const editorIsOpen = !!(document == null ? void 0 : document.getElementById(EDITOR_ID));
    const tableMenuOpen = !!(document == null ? void 0 : document.querySelector(
      `[${HEADER_MENU_STATE_ATTRIBUTE}=true]`
    ));
    const bodyAction = allowBodyAction && event.target === document.body && getHasSelection();
    return tableMenuOpen || editorIsOpen || !tableContainsEvent && !metaKey.current && !bodyAction;
  };
  const getHasSelection = () => {
    return !!getSelectedCellRange({ tableId });
  };
  const getRow = (rowIndex, nextRows) => {
    return buffer.getRow(rowIndex) ?? (nextRows == null ? void 0 : nextRows.find((r) => r.position === rowIndex));
  };
  const getCell = (columnIndex, row) => {
    const absoluteColumnIndex = getAbsoluteColumnIndex(columnIndex);
    const column = columns[absoluteColumnIndex];
    const bufferColumnIndex = (column == null ? void 0 : column.index) ?? absoluteColumnIndex;
    return {
      bufferColumnIndex,
      column,
      cell: row == null ? void 0 : row.cells[bufferColumnIndex]
    };
  };
  const getIsGhostRow = (row) => {
    return !row && !!rowCounts.current.additional;
  };
  const getBulkSelectedRowCount = () => {
    const selection2 = bulkSelection.current;
    if (!selection2)
      return 0;
    const exceptions = Object.values(selection2.exceptions);
    if (selection2.type === "none") {
      return exceptions.length;
    }
    if (selection2.type === "all") {
      return rowCounts.current.total - exceptions.length;
    }
    return 0;
  };
  const getBulkSelectedRows = () => {
    const selection2 = bulkSelection.current;
    if (!selection2)
      return [];
    if (selection2.type === "none") {
      return Object.values(selection2.exceptions).filter(
        (row) => !!row
      );
    }
    return buffer.bufferedRecords.filter((row) => {
      return !selection2.exceptions[row.position];
    });
  };
  const getCellChange = (columnIndex, rowIndex, row, options, ghost) => {
    const { column, cell, bufferColumnIndex } = getCell(columnIndex, row);
    const columnDisabled = column == null ? void 0 : column.disableClipboardEvents;
    const disabled2 = (cell == null ? void 0 : cell.active) || (cell == null ? void 0 : cell.disabled) || (column == null ? void 0 : column.disabled) || (options && options.ignoreDisableClipboardEvents ? false : columnDisabled);
    if (columnIndex > -1 && !(disabled2 && (options == null ? void 0 : options.filterDisabled)) && !(ghost && (options == null ? void 0 : options.filterGhost))) {
      const value = (options == null ? void 0 : options.value) !== void 0 ? options == null ? void 0 : options.value : cell == null ? void 0 : cell.value;
      return buildCellChange({
        position: rowIndex,
        column: (options == null ? void 0 : options.useAbsoluteColumnIndex) ? bufferColumnIndex : columnIndex,
        columnKey: column == null ? void 0 : column.value,
        value: getEditedValue(value, column) ?? value,
        row,
        disabled: disabled2,
        columnDisabled,
        ghost
      });
    }
    return void 0;
  };
  const getBulkSelectedCells = (options) => {
    const selectedCells = [];
    const rows = getBulkSelectedRows();
    for (let i = 0; i < rows.length; i++) {
      const row = rows[i];
      for (let columnIndex = 0; columnIndex < columns.length; columnIndex++) {
        const change = getCellChange(columnIndex, row.position, row, options);
        if (change)
          selectedCells.push(change);
      }
    }
    return selectedCells;
  };
  const getSelectedCells = (options, nextRows) => {
    const selectedCells = [];
    const selectedCellRange = getSelectedCellRange({ sort: true, tableId });
    if (!selectedCellRange) {
      if (isBulkSelecting.current) {
        return getBulkSelectedCells(options);
      }
      return selectedCells;
    }
    const start = selectedCellRange[0];
    const end = selectedCellRange[1];
    const startRowIndex = start[0] - 1 === -1 ? 0 : start[0] - 1;
    for (let rowIndex = startRowIndex; rowIndex < end[0]; rowIndex++) {
      const row = getRow(rowIndex, nextRows);
      const ghost = getIsGhostRow(row);
      for (let columnIndex = start[1]; columnIndex <= end[1]; columnIndex++) {
        const change = getCellChange(columnIndex, rowIndex, row, options, ghost);
        if (change)
          selectedCells.push(change);
      }
    }
    return selectedCells;
  };
  const onKeyDown = (event) => {
    if (getTableContainsEvent(event) && isMetaKeyEvent(event)) {
      metaKey.current = true;
    } else if (metaKey.current && !isHasMetaKeyEvent(event)) {
      metaKey.current = false;
    }
    if (disabled || getPreventKeyActions(event, true)) {
      return;
    }
    if (isBulkSelecting.current) {
      if (event.key === "Escape" && exitSelectionState) {
        event.preventDefault();
        event.stopPropagation();
        exitSelectionState(isBulkSelecting.current);
      }
      return;
    }
    if (event.key === "Backspace" || event.key === "Delete") {
      event.preventDefault();
      event.stopPropagation();
      const { rowsToCopyCount } = getCopyActionMeta();
      if (rowsToCopyCount > COPY_PASTE_LIMIT) {
        deleteAlertModal.open();
        return;
      }
      const payload = getSelectedCells({
        useAbsoluteColumnIndex: true,
        filterDisabled: true,
        filterGhost: true,
        value: "",
        ignoreDisableClipboardEvents: true
      });
      if (payload.length) {
        buffer.updateCells(payload);
      }
    }
    if (event.key === "z" && (event.ctrlKey || event.metaKey)) {
      event.preventDefault();
      event.stopPropagation();
      const history = buffer.useLatestCellHistory();
      if (history) {
        buffer.updateCells(history, true);
      }
    }
  };
  const getCopyActionMeta = () => {
    var _a;
    let firstSelectedRow = 0;
    let lastSelectedRow = 0;
    let rowsToCopyCount = 0;
    let columnsToCopyCount = 0;
    let recordsCopied = false;
    const selectedCellRange = getSelectedCellRange({ sort: true, tableId });
    if (selectedCellRange) {
      firstSelectedRow = selectedCellRange[0][0] - 1;
      lastSelectedRow = selectedCellRange[1][0];
      const firstSelectedColumn = selectedCellRange[0][1] - 1;
      const lastSelectedColumm = selectedCellRange[1][1];
      rowsToCopyCount = lastSelectedRow - firstSelectedRow;
      columnsToCopyCount = lastSelectedColumm - firstSelectedColumn;
      recordsCopied = rowsAreSelected(selectedCellRange, columns == null ? void 0 : columns.length);
    } else if (isBulkSelecting.current) {
      const rows = getBulkSelectedRows();
      firstSelectedRow = ((_a = rows[0]) == null ? void 0 : _a.position) ?? 0;
      rowsToCopyCount = getBulkSelectedRowCount();
      recordsCopied = true;
    }
    const copyType = recordsCopied ? "record" : "cell";
    const totalCopyCount = recordsCopied ? rowsToCopyCount : rowsToCopyCount * columnsToCopyCount;
    return {
      firstSelectedRow,
      rowsToCopyCount,
      totalCopyCount,
      copyType
    };
  };
  const onCopy = async (event) => {
    if (getPreventKeyActions(event)) {
      metaKey.current = false;
      return false;
    }
    event.preventDefault();
    event.stopPropagation();
    metaKey.current = false;
    return handleCopy(event);
  };
  const onCut = async (event) => {
    const copyEnabled = await onCopy(event);
    if (disabled || isBulkSelecting.current || !copyEnabled)
      return;
    const payload = getSelectedCells({
      useAbsoluteColumnIndex: true,
      filterDisabled: true,
      filterGhost: true,
      value: null
    });
    if (payload.length) {
      buffer.updateCells(payload);
    }
  };
  const onPaste = async (event) => {
    if (disabled || getPreventKeyActions(event, true)) {
      metaKey.current = false;
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    metaKey.current = false;
    return handlePaste(event);
  };
  useEffect(() => {
    document == null ? void 0 : document.addEventListener("copy", onCopy);
    document == null ? void 0 : document.addEventListener("cut", onCut);
    document == null ? void 0 : document.addEventListener("paste", onPaste);
    document == null ? void 0 : document.addEventListener("keydown", onKeyDown);
    return () => {
      document == null ? void 0 : document.removeEventListener("copy", onCopy);
      document == null ? void 0 : document.removeEventListener("cut", onCut);
      document == null ? void 0 : document.removeEventListener("paste", onPaste);
      document == null ? void 0 : document.removeEventListener("keydown", onKeyDown);
    };
  }, [buffer, disabled, columns, getAbsoluteColumnIndex]);
  return {
    onCopy,
    onCut,
    onPaste,
    onKeyDown,
    copyAlertModal,
    pasteAlertModal,
    deleteAlertModal
  };
};
const [KeyActionsProvider, useKeyActionsContext] = constate(useKeyActions);
export {
  COPY_PASTE_LIMIT,
  KeyActionsProvider,
  useKeyActionsContext
};
